/**
 * Wishlist — localStorage + Klaviyo sync
 *
 * localStorage format (v2):
 *   JSON array of { handle, addedAt, originalPrice }
 *
 * Migrates from v1 (comma-delimited handles) on first load.
 */
const STORAGE_KEY = 'shopify-wishlist';
const BUTTON_ACTIVE_CLASS = 'active';
const SHEET_MODAL_ID = 'WishlistSheet';
let sheetInstance = null;

const selectors = {
  button: '[button-wishlist]',
  grid: '[grid-wishlist]',
  productCard: '.product-card',
  headerBubble: '.header-wishlist .cart-count-bubble',
  headerCounter: '.header-wishlist .cart-count-bubble > span',
  emptyState: '[data-wishlist-empty]',
  pageHeader: '[data-wishlist-header]',
  itemCount: '[data-wishlist-count]',
};

/* ── localStorage helpers ─────────────────────────────── */

function readStorage() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];

  // Try parsing as JSON (new v2 format)
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Validate each item has a clean handle (no JSON fragments)
      const valid = parsed.filter(function (i) {
        return i && typeof i.handle === 'string' && i.handle.length > 0 && !i.handle.includes('{') && !i.handle.includes('"');
      });
      if (valid.length > 0) return valid;

      // Array exists but all items are invalid — try to extract handles
      var recovered = [];
      parsed.forEach(function (i) {
        if (i && typeof i.handle === 'string' && i.handle.length > 0 && !i.handle.includes('{') && !i.handle.includes('"')) {
          recovered.push(i);
        }
      });
      if (recovered.length > 0) {
        writeStorage(recovered);
        return recovered;
      }
    }
  } catch (_) {
    // Not JSON — old comma-delimited format
  }

  // Migrate v1 → v2: extract clean handles from comma-delimited string
  // Handle edge case where JSON was stored and re-read as comma-delimited
  var handles = [];
  var handleMatch = raw.match(/"handle":"([^"]+)"/g);
  if (handleMatch) {
    // Corrupted JSON stored as string — extract handles from it
    handleMatch.forEach(function (m) {
      var h = m.replace('"handle":"', '').replace('"', '');
      if (h && !handles.includes(h)) handles.push(h);
    });
  } else {
    // True v1 format: simple comma-delimited handles
    raw.split(',').filter(Boolean).forEach(function (h) {
      var clean = h.trim();
      if (clean && !clean.includes('{') && !clean.includes('"') && !handles.includes(clean)) {
        handles.push(clean);
      }
    });
  }

  var migrated = handles.map(function (handle) {
    return { handle: handle, addedAt: null, originalPrice: null };
  });
  writeStorage(migrated);
  return migrated;
}

function writeStorage(items) {
  if (items.length) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/* ── Klaviyo helpers ──────────────────────────────────── */

function klaviyoTrack(eventName, properties) {
  if (window.klaviyo) {
    window.klaviyo.push(['track', eventName, properties]);
  }
}

function klaviyoSyncProfile(items) {
  if (window.klaviyo) {
    window.klaviyo.push([
      'identify',
      {
        'Wishlist Items': items.map((i) => i.handle),
        'Wishlist Count': items.length,
      },
    ]);
  }
}

/* ── Core API ─────────────────────────────────────────── */

function getAll() {
  return readStorage();
}

function getCount() {
  return readStorage().length;
}

function contains(handle) {
  return readStorage().some((item) => item.handle === handle);
}

function add(handle, opts = {}) {
  const items = readStorage();
  if (items.some((i) => i.handle === handle)) return items;

  items.push({
    handle,
    addedAt: new Date().toISOString(),
    originalPrice: opts.price ?? null,
    variantId: opts.variantId ?? null,
  });
  writeStorage(items);
  dispatchUpdate(items);

  klaviyoTrack('Wishlist Add', {
    ProductName: opts.name || handle,
    ProductID: handle,
    Price: opts.price ? opts.price / 100 : undefined,
    VariantID: opts.variantId || undefined,
    ImageURL: opts.imageUrl || undefined,
    ProductURL: opts.productUrl || '/products/' + handle,
    Source: opts.source || 'product_card',
  });
  klaviyoSyncProfile(items);

  return items;
}

function remove(handle) {
  let items = readStorage();
  items = items.filter((i) => i.handle !== handle);
  writeStorage(items);
  dispatchUpdate(items);

  klaviyoTrack('Wishlist Remove', { ProductName: handle, ProductID: handle });
  klaviyoSyncProfile(items);

  return items;
}

function toggle(handle, opts = {}) {
  if (contains(handle)) {
    return remove(handle);
  }
  return add(handle, opts);
}

function saveForLater(handle, opts = {}) {
  const items = readStorage();
  if (!items.some((i) => i.handle === handle)) {
    items.push({
      handle,
      addedAt: new Date().toISOString(),
      originalPrice: opts.price ?? null,
      variantId: opts.variantId ?? null,
    });
    writeStorage(items);
    dispatchUpdate(items);
  }

  klaviyoTrack('Save for Later', {
    ProductName: opts.name || handle,
    ProductID: handle,
    Price: opts.price ? opts.price / 100 : undefined,
    VariantID: opts.variantId || undefined,
    ImageURL: opts.imageUrl || undefined,
    ProductURL: opts.productUrl || '/products/' + handle,
    Source: 'cart_drawer',
  });
  klaviyoSyncProfile(items.length ? items : readStorage());

  return readStorage();
}

function getOriginalPrice(handle) {
  const item = readStorage().find((i) => i.handle === handle);
  return item ? item.originalPrice : null;
}

/**
 * Subscribes the visitor to the configured Klaviyo list via the official
 * client-side subscriptions API (POST /client/subscriptions/, revision
 * 2026-04-15 — earlier revisions do not accept `subscriptions` on the nested
 * profile resource). The list must be configured as Double-Opt-In in Klaviyo
 * so the visitor receives a confirmation email. Caller MUST validate DSGVO
 * consent (checkbox) before invoking — consent: "SUBSCRIBED" is sent in the
 * payload.
 *
 * After the subscribe call, the profile is identified and the wishlist items
 * are synced as profile properties so Price-Drop + Back-in-Stock flows can
 * target the visitor.
 *
 * @param {string} email - customer email address (already validated)
 * @param {string} [companyId] - Klaviyo public API key; if empty, only identify
 * @param {string} [listId] - Klaviyo list id; if empty, only identify
 * @returns {Promise<void>} resolves after subscribe + identify pushes complete
 */
function subscribeKlaviyo(email, companyId, listId) {
  const items = readStorage();

  const identifyAndTrack = () => {
    if (!window.klaviyo || !email) return;
    window.klaviyo.push(['identify', { $email: email }]);
    items.forEach((item) => {
      klaviyoTrack('Wishlist Add', {
        ProductName: item.handle,
        ProductID: item.handle,
        Price: item.originalPrice ? item.originalPrice / 100 : undefined,
        ProductURL: '/products/' + item.handle,
        Source: 'wishlist_email_opt_in',
      });
    });
    klaviyoSyncProfile(items);
  };

  if (!companyId || !listId) {
    identifyAndTrack();
    return Promise.resolve();
  }

  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Wishlist Widget',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: email,
              subscriptions: {
                email: { marketing: { consent: 'SUBSCRIBED' } },
              },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: listId } },
      },
    },
  };

  return fetch(
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
    {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: '2026-04-15',
      },
      body: JSON.stringify(payload),
    }
  )
    .then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(
          '[Wishlist] Klaviyo subscribe ' + res.status + ':',
          text,
          '\nRequest payload was:',
          JSON.stringify(payload)
        );
      }
      return res;
    })
    .catch((err) => {
      console.error('[Wishlist] Klaviyo client subscribe network error:', err);
    })
    .finally(() => {
      identifyAndTrack();
    });
}

/**
 * Checks whether the visitor is already identified to Klaviyo (has opted in
 * via this form or elsewhere in the theme). Waits up to ~3s for the Klaviyo
 * onsite SDK to finish loading. Falls back to `false` if unavailable.
 *
 * @returns {Promise<boolean>}
 */
function isKlaviyoIdentified() {
  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 30; // ~3s at 100ms
    const check = () => {
      if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
        try {
          const result = window.klaviyo.isIdentified();
          if (result && typeof result.then === 'function') {
            result.then((v) => resolve(Boolean(v))).catch(() => resolve(false));
          } else {
            resolve(Boolean(result));
          }
        } catch (_) {
          resolve(false);
        }
        return;
      }
      if (++attempts < maxAttempts) {
        setTimeout(check, 100);
      } else {
        resolve(false);
      }
    };
    check();
  });
}

/* ── Events ───────────────────────────────────────────── */

function dispatchUpdate(items) {
  document.dispatchEvent(
    new CustomEvent('shopify-wishlist:updated', {
      detail: { wishlist: items },
    })
  );
}

/* ── DOM: header bubble ───────────────────────────────── */

function updateHeaderBubble(count) {
  const bubble = document.querySelector(selectors.headerBubble);
  const counter = document.querySelector(selectors.headerCounter);
  if (!bubble || !counter) return;
  if (count <= 0) {
    bubble.setAttribute('style', 'display:none !important');
  } else {
    bubble.setAttribute('style', 'display:block !important');
  }
  counter.textContent = count;
}

/* ── DOM: wishlist page empty state + count ───────────── */

function updatePageUI(count) {
  const emptyState = document.querySelector(selectors.emptyState);
  const pageHeader = document.querySelector(selectors.pageHeader);
  const grid = document.querySelector(selectors.grid);
  const countEl = document.querySelector(selectors.itemCount);

  if (emptyState) emptyState.style.display = count === 0 ? 'flex' : 'none';
  if (pageHeader) pageHeader.style.display = count > 0 ? '' : 'none';
  if (grid) grid.style.display = count === 0 ? 'none' : '';
  if (countEl) {
    countEl.textContent = count > 0 ? count + ' Artikel' : '';
  }
}

/* ── DOM: sheet helpers ───────────────────────────────── */

function updateSheetHeader(count) {
  // The modal renders content twice (desktop widget + mobile sheet), so there
  // are two count spans in the DOM. Update all.
  const label = count === 0
    ? 'Leer'
    : (count === 1 ? '1 Artikel' : count + ' Artikel');
  document.querySelectorAll('[data-wishlist-sheet-count]').forEach((el) => {
    el.textContent = label;
  });
}

function updateSheetEmptyState(count) {
  // Same reason — two empty-state + two grid containers. Update all.
  document.querySelectorAll('[data-wishlist-sheet-empty]').forEach((el) => {
    el.style.display = count === 0 ? 'block' : 'none';
  });
  document.querySelectorAll('[data-wishlist-sheet-grid]').forEach((el) => {
    el.style.display = count === 0 ? 'none' : 'flex';
  });
}

function openSheet() {
  if (typeof BottomSheetModal === 'undefined') {
    console.warn('[Wishlist] BottomSheetModal class not loaded — cannot open sheet.');
    return;
  }
  const isDesktop = window.matchMedia('(min-width: 768px)').matches;
  if (!sheetInstance) {
    sheetInstance = new BottomSheetModal(SHEET_MODAL_ID, {
      closeOnBackdropClick: true,
      closeOnEscape: true,
      enableSwipeToClose: true,
      preventScroll: !isDesktop,
    });
  } else {
    // Re-evaluate on subsequent opens (user may have resized since last open)
    sheetInstance.options.preventScroll = !isDesktop;
  }
  const count = getCount();
  updateSheetHeader(count);
  updateSheetEmptyState(count);
  sheetInstance.open();

  // If the visitor is already identified in Klaviyo, hide the opt-in CTA
  // and show the "activated" state instead.
  isKlaviyoIdentified().then((identified) => {
    if (identified) showSheetSubscribedState();
  });
}

function closeSheet() {
  if (sheetInstance && sheetInstance.isOpen) sheetInstance.close();
}

function initSheetForm() {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-wishlist-sheet-email]');
    if (!input) return;
    const scope = input.closest('[data-wishlist-sync-form-sheet]')?.parentElement || document;
    const errorEl = scope.querySelector('[data-wishlist-sheet-error]');
    if (errorEl) errorEl.style.display = 'none';
    input.style.borderColor = '';
  });

  document.addEventListener('change', (e) => {
    const consent = e.target.closest('[data-wishlist-sheet-consent]');
    if (!consent) return;
    const scope = consent.closest('[data-wishlist-sync-form-sheet]')?.parentElement || document;
    const consentErrorEl = scope.querySelector('[data-wishlist-sheet-consent-error]');
    if (consent.checked && consentErrorEl) consentErrorEl.style.display = 'none';
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-wishlist-sync-form-sheet]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[data-wishlist-sheet-email]');
    const consent = form.querySelector('[data-wishlist-sheet-consent]');
    const button = form.querySelector('[data-wishlist-sheet-submit]');
    const scope = form.parentElement || document;
    const errorEl = scope.querySelector('[data-wishlist-sheet-error]');
    const consentErrorEl = scope.querySelector('[data-wishlist-sheet-consent-error]');
    const email = (input.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      if (errorEl) errorEl.style.display = 'block';
      input.style.borderColor = '#D32F2F';
      return;
    }
    if (consent && !consent.checked) {
      if (consentErrorEl) consentErrorEl.style.display = 'block';
      return;
    }
    if (consentErrorEl) consentErrorEl.style.display = 'none';
    input.disabled = true;
    if (consent) consent.disabled = true;
    button.disabled = true;
    button.textContent = 'Wird gespeichert...';
    const companyId = form.dataset.klaviyoCompanyId || '';
    const listId = form.dataset.klaviyoListId || '';
    subscribeKlaviyo(email, companyId, listId).then(() => {
      // Omit scope so both mobile + desktop copies of the sheet flip to the
      // subscribed state; scope=form.parentElement is too narrow here (trigger
      // + success elements are siblings of the form wrapper, not descendants).
      showSheetSubscribedState();
      if (errorEl) errorEl.style.display = 'none';
    });
  });
}

// Show the "already subscribed" state in the sheet — hide trigger + form wrapper,
// show only the success message. Applied to both mobile + desktop copies when scope omitted.
function showSheetSubscribedState(scope) {
  const scopes = scope
    ? [scope]
    : Array.from(document.querySelectorAll('#WishlistSheet [data-modal-content]'));
  scopes.forEach((sc) => {
    if (!sc) return;
    const trigger = sc.querySelector('[data-wishlist-sheet-cta-trigger]');
    const formWrap = sc.querySelector('[data-wishlist-sheet-cta-form]');
    const successEl = sc.querySelector('[data-wishlist-sheet-success]');
    if (trigger) trigger.style.display = 'none';
    if (formWrap) formWrap.style.display = 'none';
    if (successEl) successEl.style.display = 'block';
  });
}

/* ── DOM: heart buttons ───────────────────────────────── */

/* ── Wishlist page undo toast ─────────────────────────── */

var wishlistUndoToast = null;
var wishlistUndoTimeout = null;
var wishlistUndoData = null;

function ensureWishlistToast() {
  if (wishlistUndoToast && wishlistUndoToast.parentNode) return wishlistUndoToast;
  wishlistUndoToast = document.createElement('div');
  wishlistUndoToast.id = 'Wishlist-UndoToast';
  wishlistUndoToast.style.cssText = 'display:none;position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);z-index:9999;background:#121212;color:#fff;padding:1.2rem 2rem;border-radius:0.8rem;box-shadow:0 4px 20px rgba(0,0,0,0.15);max-width:40rem;width:calc(100% - 3.2rem);';
  wishlistUndoToast.innerHTML =
    '<div style="display:flex;align-items:center;justify-content:space-between;gap:1.2rem;">' +
      '<div style="display:flex;align-items:center;gap:0.8rem;">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#85a097" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
        '<span style="font-family:Roboto,sans-serif;font-size:1.4rem;letter-spacing:0.02em;" id="Wishlist-UndoToast-Text">Artikel entfernt</span>' +
      '</div>' +
      '<button type="button" id="Wishlist-UndoBtn" style="border:0;background:transparent;font-family:Roboto,sans-serif;font-size:1.4rem;font-weight:700;color:#85a097;cursor:pointer;text-decoration:underline;padding:0;white-space:nowrap;">Rückgängig</button>' +
    '</div>';
  document.body.appendChild(wishlistUndoToast);

  // Undo click handler
  document.getElementById('Wishlist-UndoBtn').addEventListener('click', function () {
    if (!wishlistUndoData) return;
    add(wishlistUndoData.handle, { price: wishlistUndoData.price, variantId: wishlistUndoData.variantId });
    wishlistUndoData = null;
    hideWishlistToast();
  });

  return wishlistUndoToast;
}

function pushSheetContentForToast(on) {
  // Add bottom padding to the sheet's scrollable content while the undo toast
  // is visible, so the last line-item's ATC button doesn't visually overlap
  // the (also black) toast.
  document.querySelectorAll('#WishlistSheet [data-modal-content]').forEach(function (el) {
    el.style.transition = 'padding-bottom 0.3s ease';
    el.style.paddingBottom = on ? '7rem' : '';
  });
}

function showWishlistToast(productName) {
  var toast = ensureWishlistToast();
  var textEl = document.getElementById('Wishlist-UndoToast-Text');
  if (textEl) textEl.textContent = (productName || 'Artikel') + ' entfernt';
  toast.style.display = 'block';
  toast.style.opacity = '0';
  toast.style.transition = 'opacity 0.3s ease';
  requestAnimationFrame(function () {
    toast.style.opacity = '1';
  });

  pushSheetContentForToast(true);

  clearTimeout(wishlistUndoTimeout);
  wishlistUndoTimeout = setTimeout(function () {
    hideWishlistToast();
    wishlistUndoData = null;
  }, 5000);
}

function hideWishlistToast() {
  if (!wishlistUndoToast) return;
  wishlistUndoToast.style.transition = 'opacity 0.3s ease';
  wishlistUndoToast.style.opacity = '0';
  setTimeout(function () {
    wishlistUndoToast.style.display = 'none';
    pushSheetContentForToast(false);
  }, 300);
  clearTimeout(wishlistUndoTimeout);
}

/* ── DOM: heart buttons ───────────────────────────────── */

function getCurrentVariantId(button) {
  // 1. Check URL (on PDP, ?variant=<id> is set when a variant is selected)
  var urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant) return urlVariant;
  // 2. Check nearest product-form or Shopify section for a variant input
  var scope = button.closest('product-form, main-product, [data-section-type="main-product"], .product, body');
  if (scope) {
    var variantInput = scope.querySelector('[name="id"]');
    if (variantInput && variantInput.value) return variantInput.value;
  }
  return null;
}

function initButtons() {
  var buttons = document.querySelectorAll(selectors.button);
  if (!buttons.length) return;

  buttons.forEach(function (button) {
    var handle = button.dataset.productHandle;
    if (!handle) return;

    if (contains(handle)) button.classList.add(BUTTON_ACTIVE_CLASS);
    else button.classList.remove(BUTTON_ACTIVE_CLASS);

    var newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);

    newButton.addEventListener('click', function () {
      var inWishlistGrid = !!newButton.closest(selectors.grid);
      if (inWishlistGrid && contains(handle)) {
        // Inside a wishlist grid (page or sheet): animate out + undo toast
        var card = newButton.closest(selectors.productCard);
        var item = readStorage().find(function (i) { return i.handle === handle; });
        wishlistUndoData = { handle: handle, price: item ? item.originalPrice : null, variantId: item ? item.variantId : null };

        if (card) {
          animateCardOut(card).then(function () {
            remove(handle);
            var title = card.querySelector('a[class*="font-bold"]');
            showWishlistToast(title ? title.textContent.trim() : null);
          });
        } else {
          remove(handle);
          showWishlistToast();
        }
      } else {
        var opts = {};
        var vId = newButton.dataset.variantId || getCurrentVariantId(newButton);
        if (vId) opts.variantId = vId;
        toggle(handle, opts);
        newButton.classList.toggle(BUTTON_ACTIVE_CLASS);
      }
    });
  });

  document.dispatchEvent(
    new CustomEvent('shopify-wishlist:init-buttons', {
      detail: { wishlist: getAll() },
    })
  );
}

/* ── DOM: remove from wishlist after add-to-cart ──────── */

function animateCardOut(card) {
  return new Promise(function (resolve) {
    var height = card.offsetHeight;
    card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    card.style.opacity = '0';
    card.style.transform = 'scale(0.95)';

    setTimeout(function () {
      card.style.transition = 'height 0.3s ease, margin 0.3s ease, padding 0.3s ease';
      card.style.height = '0';
      card.style.overflow = 'hidden';
      card.style.margin = '0';
      card.style.padding = '0';

      setTimeout(function () {
        resolve();
      }, 300);
    }, 300);
  });
}

function initWishlistCartButtons(grid) {
  grid.querySelectorAll('product-form').forEach(function (pf) {
    var form = pf.querySelector('form');
    if (!form) return;

    var card = pf.closest(selectors.productCard);
    if (!card) return;
    var wishlistBtn = card.querySelector(selectors.button);
    if (!wishlistBtn) return;
    var handle = wishlistBtn.dataset.productHandle;
    if (!handle) return;

    form.addEventListener('submit', function () {
      setTimeout(function () {
        animateCardOut(card).then(function () {
          remove(handle);
        });
      }, 500);
    });
  });
}

/* ── DOM: wishlist grid ───────────────────────────────── */

function fetchProductCardHTML(handle, view, variantId) {
  const viewParam = view || 'card';
  let url = '/products/' + handle + '?view=' + viewParam;
  if (variantId) url += '&variant=' + encodeURIComponent(variantId);
  return fetch(url)
    .then((res) => res.text())
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const card = doc.querySelector(selectors.productCard);
      return card ? card.outerHTML : '';
    })
    .catch((err) => {
      console.error('[Wishlist] Failed to load: ' + handle, err);
      return '';
    });
}

async function initGrid() {
  const grids = document.querySelectorAll(selectors.grid);
  if (!grids.length) return;

  const items = getAll();
  updatePageUI(items.length);

  for (const grid of grids) {
    const view = grid.dataset.wishlistView || 'card';

    if (items.length === 0) {
      grid.innerHTML = '';
      continue;
    }

    const cards = await Promise.all(items.map((i) => fetchProductCardHTML(i.handle, view, i.variantId)));
    grid.innerHTML = cards.join('');
    initGridDependencies(grid);
  }
}

function initGridDependencies(grid) {
  initButtons();
  initPriceDropBadges();
  initWishlistCartButtons(grid);

  // Upgrade product-form custom elements inserted via innerHTML.
  function upgradeProductForms() {
    if (customElements.get('product-form')) {
      grid.querySelectorAll('product-form').forEach(function (el) {
        customElements.upgrade(el);
      });
    } else {
      customElements.whenDefined('product-form').then(function () {
        grid.querySelectorAll('product-form').forEach(function (el) {
          customElements.upgrade(el);
        });
      });
    }
  }
  upgradeProductForms();

  // Close sheet when a line-item CTA links to the PDP
  grid.querySelectorAll('[data-wishlist-sheet-close-on-click]').forEach((el) => {
    el.addEventListener('click', () => closeSheet());
  });

  // Re-init Reviews.io rating snippets for dynamically loaded cards
  grid.querySelectorAll('button[class*="h-reviewsio-rating-snippet-"]').forEach(function (btn) {
    delete btn.dataset.ratingDone;
  });
  grid.querySelectorAll('.h-rating-skeleton').forEach(function (el) {
    el.style.display = '';
  });

  (function initReviews(attempts) {
    if (typeof ratingSnippet === 'function') {
      ratingSnippet('ruk_rating_snippet', {
        store: 'www.heidimanstore.com',
        mode: 'simple',
        color: '#ffbe00',
        linebreak: false,
        text: '',
        singularText: '',
        lang: 'de',
        usePolaris: false,
        showEmptyStars: false,
      });
      setTimeout(function () {
        if (typeof __hCheckEmptyRatings === 'function') __hCheckEmptyRatings(true);
      }, 2000);
    } else if (attempts < 20) {
      setTimeout(function () { initReviews(attempts + 1); }, 500);
    }
  })(0);

  document.dispatchEvent(
    new CustomEvent('shopify-wishlist:init-product-grid', {
      detail: { wishlist: getAll() },
    })
  );
}

/* ── DOM: price-drop badges on wishlist page ──────────── */

function initPriceDropBadges() {
  const grid = document.querySelector(selectors.grid);
  if (!grid) return;

  grid.querySelectorAll(selectors.productCard).forEach((card) => {
    const wishlistBtn = card.querySelector(selectors.button);
    if (!wishlistBtn) return;
    const handle = wishlistBtn.dataset.productHandle;
    if (!handle) return;

    const originalPrice = getOriginalPrice(handle);
    if (originalPrice === null) return;

    const xData = card.getAttribute('x-data');
    if (!xData) return;

    // Extract current price from x-data (in cents via Shopify money format)
    const priceMatch = xData.match(/price:\s*'([^']+)'/);
    if (!priceMatch) return;

    // Parse price string like "29,99 EUR" → cents
    const currentPriceStr = priceMatch[1].replace(/[^\d,\.]/g, '').replace(',', '.');
    const currentPrice = Math.round(parseFloat(currentPriceStr) * 100);

    if (originalPrice > 0 && currentPrice > 0 && currentPrice < originalPrice) {
      const badgeContainer = card.querySelector('[data-badge-container]');
      if (!badgeContainer) return;

      const badge = document.createElement('span');
      badge.className =
        'font-roboto text-center text-white text-[1rem] leading-[150%] tracking-[0.6px] flex py-[0.4rem] px-[0.6rem] justify-center items-center gap-[0.4rem] bg-[#121212] mt-[0.4rem]';
      badge.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg> PREIS GESENKT';
      badgeContainer.appendChild(badge);
    }
  });
}

/* ── Init ─────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  const count = getCount();
  initButtons();
  initGrid();
  updateHeaderBubble(count);
  initSheetForm();
  updateSheetHeader(count);
  updateSheetEmptyState(count);

  // Close sheet on desktop after successful ATC from line-item (cart drawer opens
  // via product-form.js → would overlap the wishlist widget in bottom-right).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (
        event &&
        event.source === 'product-form' &&
        sheetInstance &&
        sheetInstance.isOpen &&
        window.matchMedia('(min-width: 768px)').matches
      ) {
        closeSheet();
      }
    });
  }
});

document.addEventListener('shopify-wishlist:updated', (event) => {
  const items = event.detail.wishlist;
  initGrid();
  updateHeaderBubble(items.length);
  updatePageUI(items.length);
  updateSheetHeader(items.length);
  updateSheetEmptyState(items.length);
});

/* ── Public API (used by cart-drawer.js) ──────────────── */

window.Wishlist = {
  add,
  remove,
  toggle,
  contains,
  getAll,
  getCount,
  saveForLater,
  getOriginalPrice,
  subscribe: subscribeKlaviyo,
  isKlaviyoIdentified,
  openSheet,
  closeSheet,
};
